import React, { useEffect, useState, useCallback } from "react";
import {
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  Alert,
  Fade,
  Skeleton,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudDownloadIcon,
  InsertDriveFile as FileIcon,
  FolderOpen as FolderIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useCookies } from "react-cookie";
import configuration from "../config";
import { useSelector } from "react-redux";

const MyList = ({ setSelectedFile }) => {
  const [allFiles, setAllFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const [cookies] = useCookies(["token", "userId"]);
  const { API_BASE_URL } = configuration;
  
  const [hoveredFile, setHoveredFile] = useState(null);
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    const iconProps = { fontSize: 'small', sx: { mr: 1 } };
    
    switch (extension) {
      case 'pdf':
        return <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#f44336' }} />;
      case 'doc':
      case 'docx':
        return <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#2196f3' }} />;
      case 'xls':
      case 'xlsx':
        return <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4caf50' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ff9800' }} />;
      default:
        return <FileIcon {...iconProps} />;
    }
  };

  const getFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return 'N/A';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return `${Math.round(sizeInBytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const fetchUserFiles = useCallback(async () => {
    if (!selectedCompanyId) {
      return [];
    }
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getClientFilesDetails/${selectedCompanyId}`
      );
      
      if (response.data && response.data.success && response.data.data && response.data.data.files) {
        return response.data.data.files;
      } else if (response.data && response.data.files) {
        return response.data.files;
      } else {
        console.warn("Unexpected response structure:", response.data);
        return [];
      }
    } catch (err) {
      console.error("Error fetching files:", err);
      throw err;
    }
  }, [API_BASE_URL, selectedCompanyId]);

  const loadFiles = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const files = await fetchUserFiles();
      setAllFiles(files);
      setError(null);
    } catch (err) {
      console.error("Error in loadFiles:", err);
      setError(err.message);
      setAllFiles([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchUserFiles]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleRefresh = useCallback(() => {
    loadFiles(true);
  }, [loadFiles]);

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, []);

  const handleDownloadFile = useCallback(
    async (fileId, fileName, event) => {
      event.stopPropagation();
      
      setDownloadingFiles(prev => new Set([...prev, fileId]));
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/myFile/${fileId}`,
          {
            responseType: "blob",
            withCredentials: true,
          }
        );

        const contentDisposition = response.headers["content-disposition"];
        let downloadFileName = fileName || "file";
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch && fileNameMatch[1]) {
            downloadFileName = fileNameMatch[1];
          }
        }

        const contentType = response.headers["content-type"];
        const blob = new Blob([response.data], { type: contentType });

        if ('download' in document.createElement('a')) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", downloadFileName);
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          link.remove();
        } else {
          navigator.msSaveBlob(blob, downloadFileName);
        }
      } catch (error) {
        console.error("Error downloading file:", error);
        setError(`Failed to download ${fileName}: ${error.message}`);
      } finally {
        setDownloadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
      }
    },
    [API_BASE_URL]
  );

  const handleDownloadAllFiles = useCallback(async () => {
    if (!selectedCompanyId) {
      setError("No company selected");
      return;
    }
    
    setDownloadingFiles(prev => new Set([...prev, 'all']));
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/downloadAllFiles/${selectedCompanyId}`,
        {
          responseType: 'blob',
        }
      );

      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'files.zip';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      console.error("Error downloading all files:", error);
      setError("Failed to download files. Please try again later.");
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete('all');
        return newSet;
      });
    }
  }, [API_BASE_URL, selectedCompanyId]);

  const handleDeleteFile = useCallback(
    async (fileId, fileName, event) => {
      event.stopPropagation();
      
      if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
        return;
      }
      
      setDeletingFiles(prev => new Set([...prev, fileId]));
      
      try {
        await axios.delete(`${API_BASE_URL}/api/v1/deleteFile/${fileId}`);
        setAllFiles((prevFiles) =>
          prevFiles.filter((file) => file._id !== fileId)
        );
      } catch (error) {
        console.error("Error deleting file:", error);
        setError(`Failed to delete ${fileName}. Please try again later.`);
      } finally {
        setDeletingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
      }
    },
    [API_BASE_URL]
  );

  const handleFileClick = useCallback(
    async (file) => {
      setSelectedFile(file);
    },
    [setSelectedFile]
  );

  const LoadingSkeleton = () => (
    <Box sx={{ p: 2 }}>
      {[...Array(5)].map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="rectangular" width={40} height={20} sx={{ mr: 2 }} />
          <Skeleton variant="text" width="40%" sx={{ mr: 2 }} />
          <Skeleton variant="text" width="20%" sx={{ mr: 2 }} />
          <Skeleton variant="rectangular" width={80} height={32} sx={{ mr: 1 }} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          width: '100%',
          height: "100%",
          bgcolor: "background.default",
        }}
      >
        <Card
          sx={{
            bgcolor: "background.paper",
            width: "100%",
            height: { xs: "auto", md: "350px" },
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <FolderIcon sx={{ mr: 1 }} />
              Documents
            </Typography>
            <LoadingSkeleton />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        width:"100%",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Card
        sx={{
          bgcolor: "background.paper",
          width: "100%",
          height: { xs: "auto", md: "350px" },
          borderRadius: 2,
          boxShadow: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent
          sx={{
            height: "100%",
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
              Documents
              <Chip
                label={allFiles.length}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            </Typography>
            <Tooltip title="Refresh files">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                color="primary"
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Error Alert */}
          {error && (
            <Fade in={Boolean(error)}>
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {/* Content */}
          {Array.isArray(allFiles) && allFiles.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: 'text.secondary',
              }}
            >
              <FolderIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No documents found
              </Typography>
              <Typography variant="body2">
                Upload your files to get started
              </Typography>
            </Box>
          ) : (
            <>
              {/* Download All Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Button
                  onClick={handleDownloadAllFiles}
                  variant="contained"
                  startIcon={
                    downloadingFiles.has('all') ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CloudDownloadIcon />
                    )
                  }
                  disabled={downloadingFiles.has('all') || allFiles.length === 0}
                  sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    "&:disabled": {
                      backgroundColor: "action.disabledBackground",
                    },
                  }}
                >
                  Download All Files
                </Button>
              </Box>

              {/* Files Table */}
              <TableContainer
                component={Paper}
                sx={{
                  height: { xs: "auto", md: "calc(100% - 120px)" },
                  overflow: "auto",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Table stickyHeader size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      {!isMobile && (
                        <TableCell
                          sx={{
                            bgcolor: "grey.50",
                            fontWeight: 600,
                            width: "8%",
                          }}
                        >
                          #
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          bgcolor: "grey.50",
                          fontWeight: 600,
                          width: isMobile ? "50%" : "40%",
                        }}
                      >
                        File Name
                      </TableCell>
                      {!isMobile && (
                        <TableCell
                          sx={{
                            bgcolor: "grey.50",
                            fontWeight: 600,
                            width: "20%",
                          }}
                        >
                          Date
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          bgcolor: "grey.50",
                          fontWeight: 600,
                          width: isMobile ? "50%" : "32%",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(allFiles) && allFiles.map((file, index) => (
                      <TableRow
                        key={file._id}
                        onClick={() => handleFileClick(file)}
                        onMouseEnter={() => setHoveredFile(file._id)}
                        onMouseLeave={() => setHoveredFile(null)}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                          backgroundColor:
                            hoveredFile === file._id
                              ? theme.palette.action.hover
                              : "transparent",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        {!isMobile && (
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {index + 1}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getFileIcon(file.fileName)}
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  wordBreak: 'break-word',
                                  lineHeight: 1.2,
                                }}
                              >
                                {file.fileName}
                              </Typography>
                              {isMobile && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  {formatDate(file.uploadDate)}
                                </Typography>
                              )}
                              {file.fileSize && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {getFileSize(file.fileSize)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(file.uploadDate)}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 1.5 }}>
                          <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={1}
                            sx={{ alignItems: isMobile ? "stretch" : "center" }}
                          >
                            <Button
                              onClick={(e) => handleDownloadFile(file._id, file.fileName, e)}
                              variant="outlined"
                              size="small"
                              startIcon={
                                downloadingFiles.has(file._id) ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <DownloadIcon />
                                )
                              }
                              disabled={downloadingFiles.has(file._id)}
                              sx={{
                                minWidth: isMobile ? "auto" : "90px",
                                fontSize: "0.75rem",
                              }}
                            >
                              {isMobile ? "" : "Download"}
                            </Button>
                            <Tooltip title="Delete file">
                              <IconButton
                                onClick={(e) => handleDeleteFile(file._id, file.fileName, e)}
                                size="small"
                                color="error"
                                disabled={deletingFiles.has(file._id)}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'error.main',
                                    color: 'error.contrastText',
                                  },
                                }}
                              >
                                {deletingFiles.has(file._id) ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MyList;